import { isNil, isString, isNumber, isNaN, startCase, get, sortBy } from "lodash-es";
import moment from "moment";
import { Link } from "react-router-dom";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import ICatalog_Item from "@models/ICatalog_Item";
import IKit_Catalog_Items_Map from "@models/IKit_Catalog_Items_Map";
import IOrder from "@models/IOrder";
import IOrder_Item from "@models/IOrder_Item";
import IOrder_ItemUI from "@models/IOrder_ItemUI";
import IOrder_Progress_State from "@models/IOrder_Progress_State";
import IOrderUI from "@models/IOrderUI";
import IPurchase_Order_Progress_State from "@models/IPurchase_Order_Progress_State";
import ISupplierModel from "@models/ISupplierModel";
import ITotalsDetails from "@models/ITotalsDetails";
import IUserUI from "@models/IUserUI";
import LeadTimeTypeEnum from "@models/LeadTimeTypeEnum";
import OrderItemTypeEnum from "@models/OrderItemTypeEnum";
import OrderSourceEnum from "@models/OrderSourceEnum";
import OrderStatesEnum from "@models/OrderStatesEnum";
import PriceUpchargeTypeEnum from "@models/PriceUpchargeTypeEnum";
import PurchaseOrderStateEnum from "@models/PurchaseOrderStateEnum";
import UserTypesEnum from "@models/UserTypesEnum";
import CatalogItemService from "./CatalogItemService";
import UserService from "./UserService";
import IVendorModel from "@models/IVendorModel";
import IWorkflow from "@models/IWorkflow";
import IDiscount from "@models/IDiscount";
import IPriceDetailUI from "@models/IPriceDetailUI";
import ICart_Item from "@models/ICart_Item";
import IPrice_Group from "@models/IPrice_Group";
import IConnection_Token from "@models/IConnection_Token";
import IUnit_Of_Measurement_Set from "@models/IUnit_Of_Measurement_Set";
import IValidateInventoryUI from "@models/IValidateInventoryUI";
import ICurrency from "@models/ICurrency";
import CurrenciesEnum from "@models/CurrenciesEnum";
import CurrenciesISOEnum from "@models/CurrenciesISOEnum";
import IVendor_Source from "@models/IVendor_Source";
import IPrice_Band_Compiled from "@models/IPrice_Band_Compiled";
import IKitDetails from "@models/IKitDetails";
import OrderRowColPropertyEnum from "@models/OrderRowColPropertyEnum";
import IOrderRowConfig from "@models/IOrderRowConfig";
import IOnDuplicateOrderEntryItemDTO from "@models/DTO/IOnDuplicateOrderEntryItemDTO";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IBulkManualPriceDTO from "@models/DTO/IBulkManualPriceDTO";

const getDefaultOrderSteps = (supplierModel: ISupplierModel, orderStateId: OrderStatesEnum | null): IOrder_Progress_State[] => {
    const orderSteps: IOrder_Progress_State[] = [
        {
            name: "preliminary",
            step_no: 1,
            order_state_id: OrderStatesEnum.PRELIMINARY,
        },
        {
            name: "submitted",
            step_no: 2,
            order_state_id: OrderStatesEnum.SUBMITTED,
            isVendorStep: true,
            isVendorReadOnly: true,
            isSupplierReadOnly: true,
        },
        {
            name: "confirmed",
            step_no: 3,
            order_state_id: OrderStatesEnum.CONFIRMED,
            isAdminOnlyStep: true,
        },
        {
            name: "completed",
            step_no: 4,
            order_state_id: OrderStatesEnum.COMPLETED,
            isSupplierAdminReadOnly: true,
            isAdminOnlyStep: true,
        },
    ];

    if (supplierModel.has_shipments) {
        orderSteps.push({
            name: "ready_to_ship",
            order_state_id: OrderStatesEnum.READY_TO_SHIP,
            step_no: 5,
            autoCreateShipment: false,
            isAdminOnlyStep: true,
            notMovable: true,
            hideInWorkflowEditor: true,
        });

        // When order is partially shipped, show "partially_shipped" instead of "shipped"
        if (orderStateId === OrderStatesEnum.PARTIALLY_SHIPPED) {
            orderSteps.push({
                name: "partially_shipped",
                step_no: 6,
                order_state_id: OrderStatesEnum.PARTIALLY_SHIPPED,
                autoCreateShipment: false,
                isAdminOnlyStep: true,
                notMovable: true,
                hideInWorkflowEditor: true,
            });
        } else {
            orderSteps.push({
                name: "shipped",
                step_no: 6,
                order_state_id: OrderStatesEnum.SHIPPED,
                autoCreateShipment: false,
                isAdminOnlyStep: true,
                notMovable: true,
                hideInWorkflowEditor: true,
            });
        }
    }

    return orderSteps;
};

const getPurchaseOrderSteps = (supplierModel: ISupplierModel): IPurchase_Order_Progress_State[] => {
    const defaultPurchaseOrderSteps: IPurchase_Order_Progress_State[] = [
        {
            name: "preliminary",
            order_state_id: PurchaseOrderStateEnum.PRELIMINARY,
            step_no: 1,
        },
        {
            name: "submitted",
            order_state_id: PurchaseOrderStateEnum.SUBMITTED,
            step_no: 2,
            isReadOnly: true,
        },
        {
            name: "incoming",
            order_state_id: PurchaseOrderStateEnum.INCOMING,
            step_no: 3,
            isReadOnly: true,
        },
        {
            name: "received",
            order_state_id: PurchaseOrderStateEnum.RECEIVED,
            step_no: 4,
            isReadOnly: true,
        },
    ];

    return defaultPurchaseOrderSteps;
};

const segmentOrderStates = (
    states: IOrder_Progress_State[]
): { mainWorkflow: IOrder_Progress_State[]; fulfillmentWorkflow: IOrder_Progress_State[]; finalStep?: IOrder_Progress_State } => {
    let shippingIndex = 0;
    let index = 0;

    const statesNoFinalStep = states.filter((state) => !state.isFinalStep);

    for (const state of statesNoFinalStep) {
        if (state.order_state_id === OrderStatesEnum.SHIPPED) {
            shippingIndex = index;
        }

        index++;
    }

    if (!shippingIndex) {
        index = 0;

        for (const state of statesNoFinalStep) {
            if (state.order_state_id === OrderStatesEnum.READY_FOR_PICKUP) {
                shippingIndex = index;
            }

            index++;
        }

        let mainWorkflow: IOrder_Progress_State[] = [];
        let fulfillmentWorkflow: IOrder_Progress_State[] = [];
        let finalStep: IOrder_Progress_State | undefined;

        if (shippingIndex) {
            mainWorkflow = statesNoFinalStep.slice(0, shippingIndex);
            fulfillmentWorkflow = statesNoFinalStep.slice(shippingIndex, statesNoFinalStep.length - 1);
        } else {
            mainWorkflow = statesNoFinalStep;
        }

        for (const orderState of states) {
            if (orderState.isFinalStep) {
                finalStep = orderState;
            }
        }

        return { mainWorkflow, fulfillmentWorkflow, finalStep };
    } else {
        const mainWorkflow = statesNoFinalStep.slice(0, shippingIndex);
        const fulfillmentWorkflow = statesNoFinalStep.slice(shippingIndex, statesNoFinalStep.length - 1);
        const finalStep = states.find((state) => state.isFinalStep);

        return { mainWorkflow, fulfillmentWorkflow, finalStep };
    }
};

const getCurrentOrderStep = (order: IOrder, supplierModel: ISupplierModel): IOrder_Progress_State | undefined => {
    const orderSteps = getOrderSteps(supplierModel, order.order_state_id || null, order.Workflow?.detail);

    return orderSteps.find((step) => step.order_state_id === order.order_state_id);
};

/**
 * Get the Order Steps for the Supplier
 *
 * @param supplierModel
 * @param orderStateId
 * @param segment
 */
const getOrderSteps = (
    supplierModel: ISupplierModel,
    orderStateId: OrderStatesEnum | null,
    stepsOverride?: IOrder_Progress_State[],
    order?: IOrder
): IOrder_Progress_State[] => {
    let states = supplierModel.order_progress_states;

    if (!states) {
        states = getDefaultOrderSteps(supplierModel, orderStateId);
    }

    if (stepsOverride) {
        states = stepsOverride;
    }

    if (supplierModel.has_local_delivery) {
        let hasDeliveryItems = false;

        for (const state of states) {
            if (state.order_state_id === OrderStatesEnum.DELIVERED) {
                hasDeliveryItems = true;
            }
        }

        if (!hasDeliveryItems) {
            states.push({
                order_state_id: OrderStatesEnum.READY_FOR_DELIVERY,
                name: "ready_for_delivery",
                step_no: states.length,
                notMovable: true,
                hideInWorkflowEditor: true,
            });

            states.push({
                order_state_id: OrderStatesEnum.DELIVERED,
                name: "delivered",
                step_no: states.length + 1,
                notMovable: true,
                hideInWorkflowEditor: true,
            });
        }
    }

    if (supplierModel.has_local_pickup) {
        let hasPickupItems = false;

        for (const state of states) {
            if (state.order_state_id === OrderStatesEnum.PICKED_UP) {
                hasPickupItems = true;
            }
        }

        if (!hasPickupItems) {
            states.push({
                order_state_id: OrderStatesEnum.READY_FOR_PICKUP,
                name: "ready_for_pickup",
                step_no: states.length,
                notMovable: true,
                hideInWorkflowEditor: true,
            });

            states.push({
                order_state_id: OrderStatesEnum.PICKED_UP,
                name: "picked_up",
                step_no: states.length + 1,
                notMovable: true,
                hideInWorkflowEditor: true,
            });
        }
    }

    // Filter states based on order delivery type
    if (order) {
        if (order.is_local_pickup) {
            // Local pickup: filter out shipping and local delivery states
            states = states.filter(
                (state) =>
                    state.order_state_id !== OrderStatesEnum.READY_TO_SHIP &&
                    state.order_state_id !== OrderStatesEnum.SHIPPED &&
                    state.order_state_id !== OrderStatesEnum.READY_FOR_DELIVERY &&
                    state.order_state_id !== OrderStatesEnum.DELIVERED
            );
        } else if (order.is_local_delivery) {
            // Local delivery: filter out shipping and local pickup states
            states = states.filter(
                (state) =>
                    state.order_state_id !== OrderStatesEnum.READY_TO_SHIP &&
                    state.order_state_id !== OrderStatesEnum.SHIPPED &&
                    state.order_state_id !== OrderStatesEnum.READY_FOR_PICKUP &&
                    state.order_state_id !== OrderStatesEnum.PICKED_UP
            );
        } else {
            // Regular shipping: filter out local delivery and local pickup states
            states = states.filter(
                (state) =>
                    state.order_state_id !== OrderStatesEnum.READY_FOR_DELIVERY &&
                    state.order_state_id !== OrderStatesEnum.DELIVERED &&
                    state.order_state_id !== OrderStatesEnum.READY_FOR_PICKUP &&
                    state.order_state_id !== OrderStatesEnum.PICKED_UP
            );
        }
    }

    return states;
};

const nonInventoryItemQtyValidation = (
    item: ICatalog_Item,
    supplierModel: ISupplierModel,
    itemQty: number,
    existingOrderQty: number
): IValidateInventoryUI => {
    const details: IValidateInventoryUI = {
        enableAdd: false,
        qty: itemQty,
        warehouseInventoryAvailable: true,
        hasBackorderedQty: false,
        backorderQty: 0,
        maxQty: itemQty,
        showInventoryMessage: false,
        inventoryMessage: { title: "", className: "" },
    };

    if (!isNil(item?.min_order_qty)) {
        if (supplierModel.allow_neg_order_qty || (Number(itemQty) && Number(itemQty) + existingOrderQty >= Number(item?.min_order_qty))) {
            details.enableAdd = true;
        } else {
            details.enableAdd = false;
        }
    } else {
        if (supplierModel.allow_neg_order_qty || (Number(itemQty) && Number(itemQty) > 0)) {
            details.enableAdd = true;
        }
    }

    if (item?.max_order_qty) {
        if (itemQty > item.max_order_qty) {
            details.enableAdd = false;
            details.maxQty = item.max_order_qty;
            // details.qty = item.max_order_qty;
        }
    }

    return details;
};

const getTotalQtyInOrderItems = (orderItems: IOrder_Item[], item: ICatalog_Item, existingItemOrderQty: number) => {
    // Check if the item has inventory flag turned on
    let inOrderQtyFound = 0;

    orderItems.map((orderItem) => {
        if (item && orderItem.Catalog_Item!.guid === item.guid) {
            // mark to check if item has uom set qty for the entered qty?
            inOrderQtyFound += Number(orderItem.qty);
        }
    });

    console.log("inOrderQtyFound - existingItemOrderQty: ", inOrderQtyFound, existingItemOrderQty);

    // we want to remove the existing item order qty.
    inOrderQtyFound = inOrderQtyFound - existingItemOrderQty;

    return inOrderQtyFound;
};

const getItemQtyOnHand = (item: ICatalog_Item, order: IOrder, orderItems: IOrder_Item[] = []) => {
    let itemQtyOnHand = item.qty_on_hand || 0;

    if (item.Material_Catalog_Item_Maps?.length) {
        itemQtyOnHand = CatalogItemService.getItemQtyOnHandBasedOnMaterials(item);
    }

    if (order.order_state_id !== OrderStatesEnum.PRELIMINARY) {
        // we need to add the reserved qty on hand
        for (const orderItem of orderItems) {
            if (orderItem.Catalog_Item?.guid === item.guid) {
                itemQtyOnHand += Number(orderItem.qty_reserved);
            }
        }
    }

    return itemQtyOnHand;
};

const getWarehouseQtyForItem = (
    details: IValidateInventoryUI,
    item: ICatalog_Item,
    order: IOrder,
    qty: number,
    inOrderQtyFound: number
) => {
    let warehouseFound = false;

    for (const mapItem of item.Warehouse_Catalog_Items_Maps || []) {
        if (mapItem.warehouse_id === order.shipping_warehouse_id) {
            warehouseFound = true;
            const warehouseQtyOnHand = mapItem.qty_on_hand;

            if (qty > warehouseQtyOnHand - inOrderQtyFound) {
                details.showInventoryMessage = true;
                details.warehouseInventoryAvailable = false;
                details.inventoryMessage.title = "inventory";
                details.inventoryMessage.className = "text-warning";
                details.maxQty = warehouseQtyOnHand - inOrderQtyFound;
                // TODO why is this set to if it's equal to the warehouse qty on hand?
                // And why do we show the inventory message?
            } else if (qty === warehouseQtyOnHand) {
                details.maxQty = warehouseQtyOnHand;
                details.showInventoryMessage = true;
                details.warehouseInventoryAvailable = true;
                details.inventoryMessage.title = "inventory";
                details.inventoryMessage.className = "text-warning";
            } else {
                details.warehouseInventoryAvailable = true;
                details.showInventoryMessage = false;
                details.inventoryMessage = { title: "", className: "" };
            }
        }
    }

    if (!warehouseFound) {
        details.warehouseInventoryAvailable = false;
        details.showInventoryMessage = true;
        details.inventoryMessage.title = "inventory";
        details.inventoryMessage.className = "text-warning";
    }

    return details;
};

const getNonWarehouseQtyForItem = (
    details: IValidateInventoryUI,
    item: ICatalog_Item,
    order: IOrder,
    qty: number,
    itemQtyOnHand: number,
    inOrderQtyFound: number,
    uomSet?: IUnit_Of_Measurement_Set
) => {
    if (qty > itemQtyOnHand - inOrderQtyFound) {
        if (uomSet) {
            let availSetQty = (itemQtyOnHand - inOrderQtyFound) / uomSet!.conversion_factor;

            if (item.unit_incr_qty === 1 || !item.unit_incr_qty) {
                availSetQty = availSetQty > 1 ? Math.round(availSetQty) : Math.ceil(availSetQty);
            }

            // Sometimes because of the UOM Sets, the qty can be off due to rounding. So we need to adjust it down.
            if (availSetQty * uomSet!.conversion_factor > itemQtyOnHand - inOrderQtyFound) {
                availSetQty = availSetQty - 1;
            }

            details.qty = availSetQty;
            details.maxQty = availSetQty;
        } else {
            details.qty = itemQtyOnHand - inOrderQtyFound;
            details.maxQty = itemQtyOnHand - inOrderQtyFound;
        }

        details.showInventoryMessage = true;
        details.inventoryMessage.title = "inventory";
        details.inventoryMessage.className = "text-warning";
    } else if (qty === itemQtyOnHand) {
        details.showInventoryMessage = true;
        details.inventoryMessage.title = "inventory";
        details.inventoryMessage.className = "text-warning";
    } else {
        details.inventoryMessage = { title: "", className: "" };
        if (uomSet) {
            details.maxQty = Math.round(qty / uomSet.conversion_factor);
        } else {
            details.maxQty = qty;
        }
    }
};

const getWarehouseBackorderedQty = (
    details: IValidateInventoryUI,
    item: ICatalog_Item,
    order: IOrder,
    qty: number,
    uomSet?: IUnit_Of_Measurement_Set
) => {
    let warehouseFound = false;

    for (const mapItem of item.Warehouse_Catalog_Items_Maps || []) {
        if (mapItem.warehouse_id === order.shipping_warehouse_id) {
            warehouseFound = true;

            if (qty > mapItem.qty_on_hand) {
                // TODO setup to deal with UOM Sets

                details.backorderQty = qty - mapItem.qty_on_hand;
                details.maxQty = qty;

                if (details.backorderQty) {
                    details.hasBackorderedQty = true;
                }
            }
        }
    }
};

const determineMaxOrderQty = (details: IValidateInventoryUI, item: ICatalog_Item, qty: number) => {
    if (item.max_order_qty) {
        if (qty > item.max_order_qty) {
            details.enableAdd = false;

            // TODO handle with uom set
            details.maxQty = item.max_order_qty;
            qty = item.max_order_qty;
        }
    }
};

/**
 * Perform an inventory validation with the order
 * @param item          The Catalog Item to perform the Inventory Validation for
 * @param qty           The Qty the user is looking to add to an order
 * @param orderItems    The Order Items - Used to validate if there is already that item in the order
 * @param supplierModel The Supplier Model
 * @param order         The Order in which the item is being added
 * @param existingItemQty The existing qty of the item in the order. If we do include the items in orderItems, we want to subtract out what's already in the order for this item.
 * @param uomSet        The UoM Set if the item has one
 *
 * @return object       The object with details on what can be done
 * enableAdd                    If the ability to add the item should be available
 * qty                          The qty that could be added if enable add is allowed
 * inventoryMessage             A potential message that could be showed based on the inventory levels
 * warehouseInventoryAvailable  If there is enough inventory available in the warehouse (if has warehouses turned on)
 * hasBackorderedQty            If there are items that will be on backorder
 * backorderQty                 The number of items that would be on backorder
 * maxQty                       The maximum amount the user can actually purchase
 */
const doInventoryValidation = (
    item: ICatalog_Item | undefined,
    qty: number,
    orderItems: IOrder_Item[],
    supplierModel: ISupplierModel,
    order: IOrder,
    existingItemQty: number,
    uomSet?: IUnit_Of_Measurement_Set
): IValidateInventoryUI => {
    const debug = false;

    const details: IValidateInventoryUI = {
        enableAdd: false,
        qty,
        warehouseInventoryAvailable: true,
        hasBackorderedQty: false,
        backorderQty: 0,
        maxQty: qty,
        inventoryMessage: { title: "", className: "" },
        showInventoryMessage: false,
    };

    let hasUomSet = false;
    let calculatedQty = qty;
    const origQty = qty;

    let calculatedExistingItemQty = existingItemQty;

    if (debug) {
        console.log(
            "Initial detailQty: ",
            details.qty,
            "calculatedQty: ",
            calculatedQty,
            "calculatedExistingItemQty: ",
            calculatedExistingItemQty
        );
    }

    if (uomSet) {
        if (uomSet.conversion_factor) {
            hasUomSet = true;
            calculatedQty = uomSet.conversion_factor * qty;
            calculatedExistingItemQty = uomSet.conversion_factor * existingItemQty;
            // qty = uomSet.conversion_factor * qty;
        }
    }

    if (debug) {
        console.log(
            "doInventoryValidation: detailQty: ",
            details.qty,
            "calculatedQty: ",
            calculatedQty,
            "calculatedExistingItemQty: ",
            calculatedExistingItemQty
        );
    }

    // this caused more problems than it was worth...
    const inOrderQtyFound = 0; //item? OrderService.getTotalQtyInOrderItems(orderItems, item, calculatedExistingItemQty) : 0;

    if (debug) {
        console.log("inOrderQtyFound: ", inOrderQtyFound);
    }

    // if on, then check the current qty entered. Only allow up to a maximum of qty on hand, unless backorder. If backorder, tell user qty that will be on backorder
    if (item?.has_inventory) {
        const itemQtyOnHand = OrderEntryService.getItemQtyOnHand(item, order, orderItems);

        if (qty && !item.allow_backorder && !isNil(itemQtyOnHand)) {
            // We need to validate if the item has already been added in the order items
            if (supplierModel.has_warehouses && order.shipping_warehouse_id) {
                OrderEntryService.getWarehouseQtyForItem(details, item, order, calculatedQty, inOrderQtyFound);
            } else {
                OrderEntryService.getNonWarehouseQtyForItem(details, item, order, calculatedQty, itemQtyOnHand, inOrderQtyFound, uomSet);
            }
        }

        if (item.allow_backorder) {
            if (supplierModel.has_warehouses) {
                OrderEntryService.getWarehouseBackorderedQty(details, item, order, calculatedQty, uomSet);
            } else {
                // TODO do anything?
            }
        }

        // Unit Increment Auto Adjustments
        if (item.unit_incr_qty && item.unit_incr_qty > 1) {
            const remainder = qty % item.unit_incr_qty;

            if (remainder) {
                qty = qty - remainder;
            }
        }

        if (supplierModel.has_warehouses && order.shipping_warehouse_id) {
            for (const mapItem of item.Warehouse_Catalog_Items_Maps || []) {
                if (mapItem.warehouse_id === order.shipping_warehouse_id) {
                    if (!item.allow_backorder) {
                        details.maxQty = mapItem.qty_on_hand;
                    }

                    if (!isNil(item.min_order_qty)) {
                        if (supplierModel.allow_neg_order_qty || (Number(qty) && Number(qty) >= Number(item.min_order_qty))) {
                            details.enableAdd = true;
                        } else {
                            details.enableAdd = false;
                        }
                    } else {
                        if (supplierModel.allow_neg_order_qty || (Number(qty) && Number(qty) > 0)) {
                            details.enableAdd = true;
                        }
                    }
                }
            }

            if (!details.warehouseInventoryAvailable) {
                if (item.allow_backorder) {
                    details.enableAdd = true;
                } else {
                    details.enableAdd = false;
                }
            } else {
                if (item.allow_backorder) {
                    details.enableAdd = true;
                }
            }
        } else {
            if (!isNil(item.min_order_qty)) {
                if (supplierModel.allow_neg_order_qty || (Number(calculatedQty) && Number(calculatedQty) >= Number(item.min_order_qty))) {
                    details.enableAdd = true;
                } else {
                    details.enableAdd = false;
                }
            } else {
                if (supplierModel.allow_neg_order_qty || (Number(calculatedQty) && Number(calculatedQty) > 0)) {
                    details.enableAdd = true;
                }
            }
        }

        OrderEntryService.determineMaxOrderQty(details, item, calculatedQty);

        return details;
    } else {
        if (item) {
            return OrderEntryService.nonInventoryItemQtyValidation(item, supplierModel, origQty, inOrderQtyFound);
        } else {
            return {
                enableAdd: false,
                qty: origQty,
                warehouseInventoryAvailable: false,
                hasBackorderedQty: false,
                backorderQty: 0,
                maxQty: origQty,
                inventoryMessage: { title: "", className: "" },
                showInventoryMessage: false,
            };
        }
    }
};

/**
 * @deprecated
 *
 * Use doInventoryValidation function instead.
 *
 * @param item
 * @param value
 * @param orderItems
 * @param showQtyOnHand
 * @param existingQtyOnHand
 */
const validateInventoryForItem = (
    item: ICatalog_Item,
    value: number,
    orderItems: IOrder_Item[],
    showQtyOnHand: boolean,
    existingQtyOnHand: number
): { showQtyOnHand: boolean; existingQtyOnHand: number; isQtyValid: boolean; maxOrderQty: number | undefined } => {
    let qty_on_hand = 0;
    let hasInventory = false;
    let allowBackorder = false;
    let maxOrderQty: number | undefined = undefined;

    if (item) {
        qty_on_hand = item.qty_on_hand || 0;
        hasInventory = item.has_inventory || false;
        allowBackorder = item.allow_backorder || false;
        maxOrderQty = item.max_order_qty;
    }

    console.log("validateInventoryForItem", item, qty_on_hand, hasInventory, allowBackorder);

    let isQtyValid = false;

    let existingOrderQtyOnHand = 0;

    if (orderItems.length) {
        for (const orderItem of orderItems) {
            if (orderItem.Catalog_Item?.guid === item.guid) {
                existingOrderQtyOnHand += Number(orderItem.qty);
            }
        }
    }

    const toOrderTotal = Number(value) + existingOrderQtyOnHand;

    if (hasInventory) {
        if (toOrderTotal > qty_on_hand) {
            if (allowBackorder) {
                if (maxOrderQty) {
                    if (toOrderTotal < maxOrderQty) {
                        isQtyValid = true;
                    } else {
                        isQtyValid = false;
                        showQtyOnHand = true;
                    }
                } else {
                    isQtyValid = true;
                }
            } else {
                showQtyOnHand = true;
            }
        } else if (toOrderTotal <= qty_on_hand) {
            if (maxOrderQty) {
                if (toOrderTotal < maxOrderQty) {
                    isQtyValid = true;
                } else {
                    isQtyValid = false;
                    showQtyOnHand = true;
                }
            } else {
                showQtyOnHand = false;
                isQtyValid = true;
            }
        }
    }

    console.log(
        "validateInventoryForItem toOrderTotal: ",
        toOrderTotal,
        "Qty On Hand: ",
        qty_on_hand,
        "Show Qty On Hand: ",
        showQtyOnHand,
        "Existing Qty. On Hand: ",
        existingQtyOnHand,
        "Is Valid Qty: ",
        isQtyValid
    );

    return { showQtyOnHand, existingQtyOnHand, isQtyValid, maxOrderQty };
};

const processTotals = (
    order: IOrderUI,
    supplierModel: ISupplierModel
): {
    orderUpdated: IOrderUI;
    pricing: { sub_total: number; tax: number; shipping: number; other: number; credit_card_surcharge_amount: number; grand_total: number };
} => {
    const pricing = calculateTotals(order, supplierModel);

    const { sub_total, tax, shipping, other, grand_total, credit_card_surcharge_amount, order_discount } = pricing;

    order.sub_total = sub_total;
    order.tax = tax;
    order.shipping = shipping;
    order.other = other;
    order.discount_total = order_discount;
    order.credit_card_surcharge_amount = credit_card_surcharge_amount;
    order.grand_total = grand_total;

    return { orderUpdated: order, pricing };
};

const getPriceGroupPriceDiscount = (orderItem: IOrder_ItemUI | IOrder_Item, supplierModel: ISupplierModel, order: IOrder): number => {
    let discount = 0;

    if (supplierModel.has_price_group_category_discounts) {
        if (orderItem.Catalog_Item?.Category?.price_group_discount && order?.Price_Group) {
            discount = Number(orderItem.Catalog_Item?.Category?.price_group_discount[order?.Price_Group.value]);
        }
    }

    return discount;
};

const calculateTotals = (order: IOrder, supplierModel: ISupplierModel): ITotalsDetails => {
    const isOfflineOrder = order.guid?.indexOf("offline") !== -1;

    let sub_total = 0.0;
    let tax = 0.0;
    const shipping = order.shipping || 0.0;
    const labor = order.labor || 0.0;
    const other = 0.0;
    let order_discount = 0.0;
    let credit_card_surcharge_amount = 0.0;
    let grand_total = 0.0;

    let itemTaxableAmount = 0.0;

    if (order) {
        order.Order_Items.forEach((item: IOrder_Item, index: number) => {
            let hasFrozenPrice = false;

            if (!isNil(item.list_price_frozen)) {
                if (isString(item.list_price_frozen)) {
                    // @ts-expect-error
                    if (item.list_price_frozen!.toString().length) {
                        hasFrozenPrice = true;
                    }
                } else {
                    hasFrozenPrice = true;
                }
            }

            if (hasFrozenPrice && !isOfflineOrder) {
                let price = Number(item.list_price_frozen);

                if (!isNil(item.cost_price_frozen) && !isOfflineOrder) {
                    price = Number(item.cost_price_frozen);
                }

                const discount = 0;
                //
                // if (
                //     this.state.order
                //     // this.state.order.supplierVendorToken &&
                //     // this.state.order.supplierVendorToken.price_discount_one
                // ) {
                //     let priceAdjustment =
                //         this.state.order!.price_adjustment || this.state.order!.supplierVendorToken!.price_discount_one || 0;
                //
                //     // if (this.state.order.Vendor_Price_Group_Map) {
                //     //     priceAdjustment = this.getVendorPriceGroupDiscount();
                //     // }
                //
                //     discount = priceAdjustment;
                // }

                let rowTotal = price * (1 - discount);

                if (discount > 1) {
                    rowTotal = price * discount;
                }

                // https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary
                rowTotal = Math.round(Number(rowTotal + Number.EPSILON) * 100) / 100;

                rowTotal = rowTotal * Number(item.qty);

                // Credit lines carry a negative total while qty stays positive
                if (item.is_credit) {
                    rowTotal = Math.abs(rowTotal) * -1;
                }

                sub_total += rowTotal;

                if (item.Catalog_Item?.is_taxable || item.Catalog_Item?.is_taxable === null) {
                    itemTaxableAmount += rowTotal;
                }
            } else {
                if (order.order_item_type_id === OrderItemTypeEnum.Standard) {
                    const price = OrderEntryService.getPriceForCatalogItem(order, item.Catalog_Item!, supplierModel, item);

                    const priceGroupDiscount = getPriceGroupPriceDiscount(item, supplierModel, order);

                    const adjustment = resolvePriceAdjustment(order, priceGroupDiscount || undefined);

                    let rowTotal = applyPriceAdjustment(price, adjustment.primary, adjustment.secondary);

                    rowTotal = rowTotal * Number(item.qty);

                    // Credit lines carry a negative total while qty stays positive
                    if (item.is_credit) {
                        rowTotal = Math.abs(rowTotal) * -1;
                    }

                    sub_total += rowTotal;

                    if (item.Catalog_Item?.is_taxable || item.Catalog_Item?.is_taxable === null) {
                        itemTaxableAmount += rowTotal;
                    }
                }
            }
        });
    }

    sub_total = Math.round(Number(sub_total + Number.EPSILON) * 100) / 100;

    if (order.discount) {
        if (order.discount_type_id === PriceUpchargeTypeEnum.FIXED) {
            order_discount = Number(order.discount);
        } else if (order.discount_type_id === PriceUpchargeTypeEnum.PERCENT) {
            order_discount = sub_total * (Number(order.discount) / 100);
        }
    }

    if (!order.Vendor?.tax_exempt) {
        tax = doTaxCalculation(supplierModel, itemTaxableAmount - order_discount, order.Vendor);
        tax = Math.round(tax * 100) / 100;
    }

    if (supplierModel.Supplier_Integration.has_taxjar) {
        if (order.taxjar_calc_tax_resp) {
            tax = order.taxjar_calc_tax_resp.tax.amount_to_collect;
        } else {
            tax = 0;
        }
    }

    if (order?.order_source_id !== OrderSourceEnum.ORDERWERKS) {
        tax = order?.tax || 0;
    }

    credit_card_surcharge_amount = order.credit_card_surcharge_amount || 0;

    grand_total = sub_total - order_discount + tax + shipping + other + labor + credit_card_surcharge_amount;

    return {
        sub_total,
        tax,
        shipping,
        labor,
        other,
        order_discount,
        credit_card_surcharge_amount,
        grand_total,
        taxable_amount: 0,
    };
};

const calculateRowTotal = (order: IOrder, orderItem: IOrder_Item, supplierModel: ISupplierModel): number => {
    let rowTotalPrice = 0;

    if (orderItem.total) {
        rowTotalPrice = orderItem.total;
    } else {
        const isOfflineOrder = order.guid?.indexOf("offline") !== -1;

        const priceGroupDiscount = getPriceGroupPriceDiscount(orderItem, supplierModel, order);

        const adjustment = resolvePriceAdjustment(order, priceGroupDiscount || undefined);

        let discount = adjustment.primary || 0;
        let secondaryDiscount = adjustment.secondary;

        if (discount > 1) {
            discount = discount / 100;
        }

        if (!isNumber(discount)) {
            discount = 0;
            secondaryDiscount = undefined;
        }

        if (!isNil(order?.order_state_id) && order?.order_state_id !== OrderStatesEnum.PRELIMINARY) {
            discount = 0;
            secondaryDiscount = undefined;
        }

        if (!isOfflineOrder) {
            discount = 0;
            secondaryDiscount = undefined;
        }

        // console.log("row total: discount: ", discount);

        let list_price_frozen = Number(orderItem.list_price_frozen);

        if (isNil(orderItem.list_price_frozen) || isOfflineOrder) {
            list_price_frozen = getPriceForCatalogItem(order, orderItem.Catalog_Item!, supplierModel, orderItem);
        }

        // Because we're changing the discount to 0 if we're not on preliminary,
        // we should be using the cost_price_frozen for the list_price_frozen value
        if (!isNil(orderItem.cost_price_frozen) && !isOfflineOrder) {
            list_price_frozen = Number(orderItem.cost_price_frozen);
        }

        let costPrice = (1 - discount) * Number(list_price_frozen);

        if (discount > 1) {
            costPrice = discount * Number(list_price_frozen);
        }

        // Cascade the Connection_Token's second discount (price_discount_two) for a "45+5" chain.
        if (secondaryDiscount) {
            let d2 = secondaryDiscount;

            if (d2 > 1) {
                d2 = d2 / 100;
            }

            costPrice = (1 - d2) * costPrice;
        }

        // https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary
        let roundFactor = 100;

        if (supplierModel.pricing_precision) {
            switch (supplierModel.pricing_precision) {
                case 0: {
                    roundFactor = 1;
                    break;
                }

                case 1: {
                    roundFactor = 10;
                    break;
                }

                case 2: {
                    roundFactor = 100;
                    break;
                }

                case 3: {
                    roundFactor = 1000;
                    break;
                }

                case 4: {
                    roundFactor = 10000;
                    break;
                }
            }
        }

        costPrice = Math.round(Number(costPrice + Number.EPSILON) * roundFactor) / roundFactor;

        rowTotalPrice = Number(orderItem.qty) * Number(costPrice);
    }

    // Credit lines carry a negative total while qty stays positive
    if (orderItem.is_credit) {
        rowTotalPrice = Math.abs(rowTotalPrice) * -1;
    }

    return rowTotalPrice;
};

/**
 * Gets the Price For The Catalog Item based on the Price Group
 * @param catalogItem
 */
const getPriceForCatalogItem = (
    order: IOrder,
    catalogItem: ICatalog_Item,
    supplierModel: ISupplierModel,
    orderItem: IOrder_Item
): number => {
    if (order && order.Price_Group && catalogItem.pricing) {
        const priceGroup = order.Price_Group.value;

        let price = Number(catalogItem.pricing[`${priceGroup}`]);

        if (supplierModel.use_msrp_for_default_price && price === 0) {
            if (catalogItem.msrp) {
                price = catalogItem.msrp;
            }
        }

        if (supplierModel.has_qty_break && catalogItem.qty_break && catalogItem.qty_break > 0) {
            if (orderItem.qty >= catalogItem.qty_break && Number(catalogItem.qty_break_price || 0) > 0) {
                price = Number(catalogItem.qty_break_price);
            }
        }

        return price;
    } else {
        return 0;
    }
};

const getVendorPriceGroupDiscount = (order?: IOrder): number => {
    let priceAdjustment = 0;

    if (order && order.Vendor_Price_Group_Map) {
        if (order.Vendor_Price_Group_Map.price_adjustment) {
            if (order.Vendor_Price_Group_Map.price_adjustment_expiration) {
                const localizedDateStr = UserService.parseDate(order.Vendor_Price_Group_Map.price_adjustment_expiration);

                const isApplicable = moment(localizedDateStr).isSameOrAfter();

                if (isApplicable) {
                    priceAdjustment = order.Vendor_Price_Group_Map.price_adjustment;
                }
            } else {
                priceAdjustment = order.Vendor_Price_Group_Map.price_adjustment;
            }
        }
    }

    if (priceAdjustment > 1) {
        priceAdjustment = Number(priceAdjustment) / 100;
    }

    return priceAdjustment;
};

interface IResolvedPriceAdjustment {
    primary: number;
    // secondary is only set when the active discount is the Connection_Token's price_discount_one,
    // which carries a companion price_discount_two for a trade "45+5" chain discount.
    secondary?: number;
}

// Resolve the active price adjustment for an order, preserving existing precedence:
// category override > Vendor_Price_Group_Map > Connection_Token.price_discount_one > order.price_adjustment.
// Only the Connection_Token source carries a secondary (price_discount_two) discount.
const resolvePriceAdjustment = (order?: IOrder, categoryOverride?: number): IResolvedPriceAdjustment => {
    if (categoryOverride != null) {
        return { primary: categoryOverride };
    }

    const vendorPriceGroupDiscount = getVendorPriceGroupDiscount(order);

    if (vendorPriceGroupDiscount) {
        return { primary: vendorPriceGroupDiscount };
    }

    const token = order?.supplierVendorToken;

    if (token?.price_discount_one) {
        return { primary: token.price_discount_one, secondary: token.price_discount_two || undefined };
    }

    if (order?.price_adjustment) {
        return { primary: order.price_adjustment };
    }

    return { primary: 0 };
};

// Apply the primary adjustment (legacy semantics preserved: a value > 1 after /100 acts as a
// multiplier) then cascade the optional secondary onto the already-reduced price (chain discount).
const applyPriceAdjustment = (price: number, primary: number, secondary?: number): number => {
    let d1 = primary;

    if (d1 > 1) {
        d1 = d1 / 100;
    }

    if (d1 > 1) {
        price = d1 * Number(price);
    } else {
        price = (1 - Number(d1)) * Number(price);
    }

    if (secondary) {
        let d2 = secondary;

        if (d2 > 1) {
            d2 = d2 / 100;
        }

        price = (1 - Number(d2)) * Number(price);
    }

    return Math.round(Number(price + Number.EPSILON) * 100) / 100;
};

const doTaxCalculation = (supplierModel: ISupplierModel, sub_total: number, vendorModel?: IVendorModel): number => {
    let { tax_rate } = supplierModel;

    if (!tax_rate) {
        tax_rate = 0;
    }

    if (tax_rate) {
        if (!isNumber(tax_rate)) {
            tax_rate = parseFloat(tax_rate);
        }

        if (vendorModel?.Item_Sales_Tax) {
            tax_rate = vendorModel.Item_Sales_Tax.rate;
        }

        if (tax_rate >= 1) {
            tax_rate = tax_rate / 100;
        }
    }

    return sub_total * tax_rate;
};

const getProfitAndMarginForItem = (
    order: IOrder,
    item: IOrder_Item,
    vendorSource?: IVendor_Source
): { profit: number; profitMargin: number; profitClassName: string } => {
    let profitClassName = "text-success";
    let profit = 0;
    let profitMargin = 0;

    const priceGroupName = order?.Price_Group?.value;

    const orderQty = Number(item.qty);
    let catalogItemCost = item.Catalog_Item?.cost_per_uom || 0;

    if (item.Catalog_Item?.has_price_bands) {
        if (item.Catalog_Item?.Price_Bands?.cost && !vendorSource) {
            const priceBand = CatalogItemService.findPriceBand(orderQty, item.Catalog_Item.Price_Bands.cost);

            if (priceBand) {
                catalogItemCost = priceBand.price;
            }
        }

        // see if there is a default vendor source
        let defaultVendorSource: IVendor_Source | undefined;
        if (item.Catalog_Item?.default_vendor_source_id && !vendorSource) {
            // find the matching id in the Vendor_Sources array
            defaultVendorSource = (item.Catalog_Item.Vendor_Sources || []).find(
                (_vendorSource: IVendor_Source) => _vendorSource.id === item.Catalog_Item?.default_vendor_source_id
            );
        } else if (vendorSource) {
            defaultVendorSource = vendorSource;
        }

        let costKeyEnding = "_cost";

        if (defaultVendorSource) {
            costKeyEnding = `${defaultVendorSource.name}_cost`;
        }

        // also check if there is a default vendor...or only 1 vendor
        // find all the keys in Price_Bands that have _cost in the name
        const costEntries = Object.entries(item.Catalog_Item.Price_Bands || {}).filter(
            (entry): entry is [string, IPrice_Band_Compiled[]] => {
                // Use entry[0] instead of destructuring
                return entry[0].endsWith(costKeyEnding);
            }
        );

        // if there is only 1 cost, then use that one. if the catalog item has a default vendor source, then use that one.
        if (costEntries.length) {
            if (costEntries.length === 1) {
                const [key, value] = costEntries[0] as [string, IPrice_Band_Compiled[]];
                const priceBand = CatalogItemService.findPriceBand(orderQty, value);

                if (priceBand) {
                    catalogItemCost = priceBand.price;
                }
            } else {
                // find the cheapest option for the qty...
                let cheapestPrice = 0;

                for (const [key, value] of costEntries) {
                    const priceBand = CatalogItemService.findPriceBand(orderQty, value);

                    if (priceBand) {
                        if (priceBand.price < cheapestPrice) {
                            cheapestPrice = priceBand.price;
                        } else if (cheapestPrice === 0) {
                            cheapestPrice = priceBand.price;
                        }
                    }
                }

                catalogItemCost = cheapestPrice;
            }
        }
    }

    let catalogItemPrice = 0;

    if (item?.Catalog_Item?.pricing && priceGroupName) {
        catalogItemPrice = Number(item.Catalog_Item.pricing[priceGroupName]);
    }

    const itemSalePrice = item.net_price ? Number(item.net_price) : catalogItemPrice;

    let itemProfitMargin = 0;

    if (itemSalePrice && catalogItemCost && orderQty) {
        profit = orderQty * (itemSalePrice - catalogItemCost);
        itemProfitMargin = ((itemSalePrice - catalogItemCost) / itemSalePrice) * 100;
    }

    profitMargin = itemProfitMargin;

    if (profit < 0) {
        profitClassName = "text-danger";
    } else if (profit > 0) {
        profitClassName = "text-success";
    } else {
        profitClassName = "text-warning";
    }

    return { profit, profitClassName, profitMargin };
};

const getProfitAndMargin = (
    order: IOrder,
    userModel: IUserUI,
    vendorSource?: IVendor_Source
): { profit: number; profitMargin: number; profitClassName: string } => {
    if (!order) {
        return { profit: 0, profitMargin: 0, profitClassName: "text-warning" };
    }

    let profitClassName = "text-success";
    let profit = 0;
    let profitMargin = 0;
    let totalCalculatedItems = 0;

    if (userModel?.isSupplierUser) {
        (order.Order_Items || []).forEach((item) => {
            if (item) {
                const itemProfitAndMargin = getProfitAndMarginForItem(order, item, vendorSource);

                profit += itemProfitAndMargin.profit;
                profitMargin += itemProfitAndMargin.profitMargin;
                totalCalculatedItems++;
            }
        });

        profitMargin = profitMargin / totalCalculatedItems;

        if (isNaN(profitMargin)) {
            profitMargin = 0;
        }

        if (profit < 0) {
            profitClassName = "text-danger";
        } else if (profit > 0) {
            profitClassName = "text-success";
        } else {
            profitClassName = "text-warning";
        }
    }

    return { profit, profitMargin, profitClassName };
};

const getPricingValue = (orderItem: IOrder_ItemUI, supplierModel: ISupplierModel, order?: IOrder): number => {
    let pricingValue = 0;

    let performDiscount = true;

    let isPulledIntegrationOrder = false;

    if (orderItem.woocommerce_line_item_id || orderItem.shopify_line_item_id || orderItem.opencart_order_product_id) {
        isPulledIntegrationOrder = true;
    }

    if (order?.order_state_id === OrderStatesEnum.PRELIMINARY && !isPulledIntegrationOrder) {
        // get the price from the catalog item and discount it as necessary.
        const token = order!.supplierVendorToken!;

        const priceGroup = order?.Price_Group?.value || undefined;

        let price = priceGroup ? orderItem.Catalog_Item?.pricing[priceGroup] : 0;

        if (supplierModel.has_qty_break && orderItem.Catalog_Item?.qty_break && orderItem.Catalog_Item?.qty_break > 0) {
            if (orderItem.qty >= orderItem.Catalog_Item?.qty_break) {
                price = Number(orderItem.Catalog_Item?.qty_break_price || 0);
            }
        }

        if (orderItem.is_manual_price && !isNil(orderItem.cost_price_frozen)) {
            price = Number(orderItem.cost_price_frozen);
            performDiscount = false;
        }

        if (supplierModel.use_msrp_for_default_price && Number(price) === 0) {
            if (orderItem.Catalog_Item?.msrp) {
                price = Number(orderItem.Catalog_Item.msrp);
            }
        }

        const categoryDiscount =
            supplierModel.has_price_group_category_discounts && orderItem.Catalog_Item?.Category?.price_group_discount && order?.Price_Group
                ? Number(orderItem.Catalog_Item?.Category?.price_group_discount[order?.Price_Group.value])
                : undefined;

        const adjustment = resolvePriceAdjustment(order, categoryDiscount);

        if (adjustment.primary) {
            performDiscount = true;
        }

        if (adjustment.primary && performDiscount) {
            price = applyPriceAdjustment(price, adjustment.primary, adjustment.secondary);
        }

        pricingValue = price;
    } else {
        pricingValue = Number(orderItem.cost_price_frozen);

        if (orderItem.is_manual_price && !isNil(orderItem.cost_price_frozen)) {
            pricingValue = Number(orderItem.cost_price_frozen);
            performDiscount = false;
        }
    }

    return pricingValue;
};

const getKitPrice = (
    kitCatalogItem: ICatalog_Item,
    kitItemMap: IKit_Catalog_Items_Map,
    supplierModel: ISupplierModel,
    order: IOrder
): number => {
    let pricingValue = 0;

    const kitPriceDetail = getDiscountedPriceForCatalogItem(kitCatalogItem || { pricing: {} }, supplierModel, order);

    let itemPrice = 0;

    if (!kitItemMap.is_kit_base_option) {
        const itemPriceDetail = getDiscountedPriceForCatalogItem(kitItemMap.Catalog_Item! || { pricing: {} }, supplierModel, order);
        itemPrice = itemPriceDetail.discountedPrice;
    }

    pricingValue = Number(kitPriceDetail.discountedPrice) + Number(itemPrice);

    return pricingValue;
};

const getDiscountedPriceForCartItem = (
    cartItem: ICart_Item,
    priceGroup: IPrice_Group,
    supplierModel: ISupplierModel,
    supplierVendorToken?: IConnection_Token
) => {
    let priceEach = 0;

    if (cartItem.Catalog_Item?.pricing && priceGroup?.value) {
        priceEach = cartItem.Catalog_Item?.pricing[priceGroup?.value];
    }

    // Category price-group discount overrides the chain discount (same precedence as the api's
    // getDiscount). A missing price-group key yields NaN, which must fall through to the chain
    // discount — resolvePriceAdjustment's null check alone would let NaN poison the price.
    const rawCategoryDiscount =
        supplierModel?.has_price_group_category_discounts && cartItem.Catalog_Item?.Category?.price_group_discount
            ? Number(cartItem.Catalog_Item.Category.price_group_discount[priceGroup?.value])
            : undefined;
    const categoryDiscount = rawCategoryDiscount && !cartItem.Catalog_Item?.skip_discount ? rawCategoryDiscount : undefined;

    // Apply the category discount or the vendor's Connection_Token chain discount (price_discount_one/two)
    // to the list price, mirroring catalog/list-view pricing. The cart is UI-only, so this is computed
    // client-side until it becomes an order. (Kit prices below carry their own net price.)
    const adjustment = resolvePriceAdjustment({ supplierVendorToken } as IOrder, categoryDiscount);

    if (adjustment.primary) {
        priceEach = applyPriceAdjustment(Number(priceEach), adjustment.primary, adjustment.secondary);
    }

    if (cartItem.discount_id && cartItem.Discount) {
        const discount = cartItem.Discount;

        if (discount.discount_type_id === PriceUpchargeTypeEnum.FIXED) {
            priceEach = priceEach - discount.discount;
        } else {
            priceEach = priceEach - priceEach * (discount.discount / 100);
        }
    }

    if (cartItem.is_kit_builder) {
        if (cartItem.Catalog_Item?.kit_add_each_item) {
            // Sum of individual items - use calculated kit_net_price
            priceEach = cartItem.kit_net_price || 0;
        }
        // else: Fixed kit price - keep priceEach from catalog item pricing (already set above)
    }

    return priceEach;
};

const getDiscountedPriceForCatalogItem = (
    catalogItem: ICatalog_Item,
    supplierModel: ISupplierModel,
    order: IOrder,
    set?: IUnit_Of_Measurement_Set
): IPriceDetailUI => {
    let discountedPrice = 0;

    let performDiscount = true;

    // get the price from the catalog item and discount it as necessary.
    const priceGroup = order?.Price_Group?.value || undefined;

    let price = priceGroup ? catalogItem.pricing[priceGroup] : 0;

    if (supplierModel.use_msrp_for_default_price && Number(price) === 0) {
        if (catalogItem.msrp) {
            price = Number(catalogItem.msrp);
        }
    }

    const categoryDiscount =
        supplierModel.has_price_group_category_discounts && catalogItem.Category?.price_group_discount && order?.Price_Group
            ? Number(catalogItem.Category?.price_group_discount[order?.Price_Group.value])
            : undefined;

    const adjustment = resolvePriceAdjustment(order, categoryDiscount);

    if (adjustment.primary) {
        performDiscount = true;
    }

    if (catalogItem.skip_discount) {
        performDiscount = false;
    }

    if (adjustment.primary && performDiscount) {
        price = applyPriceAdjustment(price, adjustment.primary, adjustment.secondary);
    }

    let selectedDiscount: IDiscount | undefined;

    if (supplierModel.has_discounts) {
        discountedPrice = price;

        if (catalogItem.Discounts) {
            const getDiscountPrice = (_price: number, discount: IDiscount): number => {
                let discountPrice = 0;

                if (discount) {
                    if (discount.discount_type_id === PriceUpchargeTypeEnum.FIXED) {
                        discountPrice = _price - discount.discount;

                        if (discountPrice < 0) {
                            discountPrice = 0;
                        }
                    } else if (discount.discount_type_id === PriceUpchargeTypeEnum.PERCENT) {
                        discountPrice = _price - _price * (discount.discount / 100);
                    }
                }

                return discountPrice;
            };

            for (const discount of catalogItem.Discounts) {
                const calculatedDiscount = getDiscountPrice(price, discount);

                if (calculatedDiscount < discountedPrice) {
                    discountedPrice = calculatedDiscount;
                    selectedDiscount = discount;
                }
            }
        }
    } else {
        discountedPrice = price;
    }

    if (set) {
        discountedPrice = discountedPrice * set.conversion_factor;
        price = price * set.conversion_factor;
    }

    return { originalPrice: price, discountedPrice, discount: selectedDiscount };
};

const getListPrice = (row: IOrder_ItemUI, order: IOrder, supplierModel: ISupplierModel) => {
    let pricingValue = 0;

    if (order.order_state_id === OrderStatesEnum.PRELIMINARY && order!.Price_Group) {
        // get the price from the catalog item and discount it as necessary.
        const token = order.supplierVendorToken;

        if (row.Catalog_Item) {
            let price = row.Catalog_Item!.pricing?.[order!.Price_Group!.value] || 0;

            if (supplierModel.use_msrp_for_default_price && Number(price) === 0) {
                if (row?.Catalog_Item?.msrp) {
                    price = row.Catalog_Item.msrp;
                }
            }

            if (row.is_manual_price && !isNil(row.list_price_frozen)) {
                price = Number(row.list_price_frozen);
            }

            pricingValue = price;
        }
    } else {
        if (row.list_price_frozen) {
            pricingValue = Number(row.list_price_frozen);

            if (row.is_manual_price && !isNil(row.list_price_frozen)) {
                pricingValue = Number(row.list_price_frozen);
            }
        }
    }

    return pricingValue;
};

const getDiscountedPrice = (price: number, order: IOrder, supplierModel: ISupplierModel, catalogItem?: ICatalog_Item): number => {
    // get the price from the catalog item and discount it as necessary.
    const categoryDiscount =
        supplierModel.has_price_group_category_discounts && catalogItem?.Category?.price_group_discount && order?.Price_Group
            ? Number(catalogItem?.Category?.price_group_discount[order?.Price_Group.value])
            : undefined;

    const adjustment = resolvePriceAdjustment(order, categoryDiscount);

    if (adjustment.primary) {
        price = applyPriceAdjustment(price, adjustment.primary, adjustment.secondary);
    }

    return price;
};

const getCostPrice = (row: IOrder_Item, order: IOrderUI, supplierModel: ISupplierModel): number => {
    let pricingValue = 0;

    if (order!.order_state_id === OrderStatesEnum.PRELIMINARY) {
        if (row.Catalog_Item && order!.Price_Group) {
            let price = row.Catalog_Item!.pricing?.[order!.Price_Group!.value] || 0;

            if (supplierModel.use_msrp_for_default_price && Number(price) === 0) {
                if (row?.Catalog_Item?.msrp) {
                    price = row.Catalog_Item.msrp;
                }
            }

            if (row.is_manual_price && !isNil(row.list_price_frozen)) {
                price = Number(row.list_price_frozen);
            }

            pricingValue = getDiscountedPrice(price, order, supplierModel, row.Catalog_Item);
        }
    } else {
        pricingValue = Number(row.cost_price_frozen);
    }

    return pricingValue;
};

const isPreviousStepPreliminary = (order: IOrder, supplierModel: ISupplierModel): boolean => {
    const previousIsPrelim = false;

    const orderSteps = OrderEntryService.getOrderSteps(supplierModel, OrderStatesEnum.SHIPPED);

    const currentStepOrderStateId = order!.order_state_id;

    let currentStepIndex = 0;

    for (let i = 0; i < orderSteps.length; ++i) {
        const orderStep = orderSteps[i];

        if (orderStep.order_state_id === currentStepOrderStateId) {
            currentStepIndex = i;
        }
    }

    // we're one step in the step indices past preliminary
    return currentStepIndex === 1;
};

const getCurrentAndNextOrderSteps = (
    order: IOrder,
    supplierModel: ISupplierModel
): { currentStep: IOrder_Progress_State | undefined; nextStep: IOrder_Progress_State | undefined } => {
    const completeOrderSteps = OrderEntryService.getOrderSteps(supplierModel, OrderStatesEnum.SHIPPED, order.Workflow?.detail);

    const currentStepOrderStateId = order.order_state_id;
    let currentStep: IOrder_Progress_State | undefined;
    let nextStep: IOrder_Progress_State | undefined;
    let finalStep: IOrder_Progress_State | undefined;

    // filter out the final step from the order steps.
    let orderSteps = completeOrderSteps.filter((orderStep) => !orderStep.isFinalStep);
    // eslint-disable-next-line prefer-const
    finalStep = completeOrderSteps.find((orderStep) => orderStep.isFinalStep);

    if (supplierModel.has_shipments) {
        if (order.is_local_pickup) {
            orderSteps = orderSteps.filter(
                (orderStep) =>
                    orderStep.order_state_id !== OrderStatesEnum.SHIPPED &&
                    orderStep.order_state_id !== OrderStatesEnum.DELIVERED &&
                    orderStep.order_state_id !== OrderStatesEnum.READY_FOR_DELIVERY
                // orderStep.order_state_id !== OrderStatesEnum.READY_TO_SHIP
            );
        } else if (order.is_local_delivery) {
            orderSteps = orderSteps.filter(
                (orderStep) =>
                    orderStep.order_state_id !== OrderStatesEnum.SHIPPED &&
                    orderStep.order_state_id !== OrderStatesEnum.PICKED_UP &&
                    orderStep.order_state_id !== OrderStatesEnum.READY_FOR_PICKUP
                // orderStep.order_state_id !== OrderStatesEnum.READY_TO_SHIP
            );
        } else {
            orderSteps = orderSteps.filter(
                (orderStep) =>
                    orderStep.order_state_id !== OrderStatesEnum.DELIVERED &&
                    orderStep.order_state_id !== OrderStatesEnum.READY_FOR_DELIVERY &&
                    orderStep.order_state_id !== OrderStatesEnum.PICKED_UP &&
                    orderStep.order_state_id !== OrderStatesEnum.READY_FOR_PICKUP
                // orderStep.order_state_id !== OrderStatesEnum.READY_TO_SHIP
            );
        }
    } else {
        orderSteps = orderSteps.filter(
            (orderStep) =>
                orderStep.order_state_id !== OrderStatesEnum.DELIVERED &&
                orderStep.order_state_id !== OrderStatesEnum.READY_FOR_DELIVERY &&
                orderStep.order_state_id !== OrderStatesEnum.PICKED_UP &&
                orderStep.order_state_id !== OrderStatesEnum.READY_FOR_PICKUP &&
                orderStep.order_state_id !== OrderStatesEnum.SHIPPED
            // orderStep.order_state_id !== OrderStatesEnum.READY_TO_SHIP
        );
    }

    if (finalStep) {
        orderSteps = [...orderSteps, finalStep];
    }

    // if the current workflow has no shipping/delivery/pickup, then the last step is the final step.
    // if (supplierModel.has_shipments || supplierModel.has_local_delivery || supplierModel.has_local_pickup) {
    //
    // }

    // if the current workflow has shipping/deliveyr/picup, then the next step is the final step

    let hasSubmittedStep = false;
    let hasConfirmedStep = false;

    for (let i = 0; i < orderSteps.length; ++i) {
        const orderStep = orderSteps[i];

        if (orderStep.order_state_id === currentStepOrderStateId) {
            currentStep = orderStep;

            if (!isNil(orderSteps[i + 1])) {
                nextStep = orderSteps[i + 1];
            }
        }

        if (orderStep.order_state_id === OrderStatesEnum.SUBMITTED) {
            hasSubmittedStep = true;
        }

        if (orderStep.order_state_id === OrderStatesEnum.CONFIRMED) {
            hasConfirmedStep = true;
        }
    }

    return { currentStep, nextStep };
};

const isOrderAtEndWorkflowStep = (order: IOrder, supplierModel: ISupplierModel): boolean => {
    const { currentStep, nextStep } = getCurrentAndNextOrderSteps(order, supplierModel);

    // Order is at end if there's no next step OR current step is marked as final
    return isNil(nextStep) || currentStep?.isFinalStep === true;
};

const getCatalogItemLeadTime = (catalogItem: ICatalog_Item): { leadTime: number | null; leadTimeType: LeadTimeTypeEnum | null } => {
    let leadTime: number | null = null;
    let leadTimeType: LeadTimeTypeEnum | null = null;

    if (catalogItem?.Catalog_Item_Vendor_Source_Maps?.length) {
        if (catalogItem?.Catalog_Item_Vendor_Source_Maps[0].lead_time) {
            leadTime = catalogItem?.Catalog_Item_Vendor_Source_Maps[0].lead_time;
            leadTimeType = catalogItem?.Catalog_Item_Vendor_Source_Maps[0].lead_time_type || null;
        } else if (catalogItem?.Catalog_Item_Vendor_Source_Maps[0]?.Vendor_Source?.default_lead_time) {
            leadTime = catalogItem?.Catalog_Item_Vendor_Source_Maps[0].Vendor_Source?.default_lead_time;
            leadTimeType = catalogItem?.Catalog_Item_Vendor_Source_Maps[0].Vendor_Source?.default_lead_time_type || null;
        }
    }

    return { leadTime, leadTimeType };
};

const submitBtnDisabled = (order: IOrder, supplierModel: ISupplierModel, userModel: IUserUI) => {
    let isSubmitBtnDisabled = false;
    let submitBtnDisabledReason: any = "";
    let disabledLink: any = null;

    if (order.order_source_id !== OrderSourceEnum.ORDERWERKS) {
        return { reasons: null, disabledLink: null, submitBtnDisabledReason: null, isSubmitBtnDisabled: false };
    }

    const reasons: any = {
        payment_type: {
            display: false,
            message: "",
        },
        ship_to: {
            display: false,
            message: "",
        },
        bill_to: {
            display: false,
            message: "",
        },
        email: {
            display: false,
            message: "",
        },
    };

    if (!isNil(order.Vendor)) {
        const reqdFields = supplierModel.required_vendor_fields;

        if (!isNil(reqdFields) && !order.Vendor.supplier_id) {
            let vendor_name = "vendor";

            if (supplierModel.vendor_name_override) {
                vendor_name = supplierModel.vendor_name_override;
            }

            const supplierVendorsLinkTo = `/${vendor_name}s?name=${order.Vendor.name}`;

            if (reqdFields.payment_type_id) {
                if (isNil(order.Vendor.payment_type_id)) {
                    isSubmitBtnDisabled = true;

                    if (!isNil(supplierModel.vendor_name_override) && supplierModel.vendor_name_override.length) {
                        const capitalCase = startCase(supplierModel.vendor_name_override);

                        const reason = `Missing ${capitalCase} Payment Type.`;

                        reasons.payment_type.display = true;
                        reasons.payment_type.message = reason;
                    } else {
                        reasons.payment_type.display = true;
                        reasons.payment_type.message = (
                            <OwTranslate defaultMessage={"missing_vendor_payment_type"} id={"missing_vendor_payment_type"} />
                        );
                    }

                    if (userModel.isSupplierUser) {
                        disabledLink = (
                            <Link to={supplierVendorsLinkTo}>
                                <OwTranslate defaultMessage={"resolve"} id={"resolve"} />
                            </Link>
                        );
                    } else {
                        disabledLink = <OwTranslate defaultMessage={"contact_supplier"} id={"contact_supplier"} />;
                    }
                }
            }

            const lengthCheck = (type: any, property: any) => {
                let value = get(order, `Vendor['${type}']['${property}']`, "");

                if (isNil(value)) {
                    value = "";
                }

                return value.length;
            };

            if (
                reqdFields.bill_to &&
                (isNil(order.Vendor.Bill_To) ||
                    !lengthCheck("Bill_To", "address_line_one") ||
                    !lengthCheck("Bill_To", "city") ||
                    !lengthCheck("Bill_To", "state") ||
                    !lengthCheck("Bill_To", "postal_code"))
            ) {
                if (!isNil(supplierModel.vendor_name_override) && supplierModel.vendor_name_override.length) {
                    const capitalCase = startCase(supplierModel.vendor_name_override);

                    const reason = `Missing ${capitalCase} Bill To Address.`;

                    reasons.bill_to.display = true;
                    reasons.bill_to.message = reason;
                } else {
                    reasons.bill_to.display = true;
                    reasons.bill_to.message = (
                        <OwTranslate defaultMessage={"missing_vendor_bill_to_address"} id={"missing_vendor_bill_to_address"} />
                    );
                }

                if (userModel.isSupplierUser) {
                    disabledLink = (
                        <Link to={supplierVendorsLinkTo}>
                            <OwTranslate defaultMessage={"resolve"} id={"resolve"} />
                        </Link>
                    );
                } else {
                    disabledLink = <OwTranslate defaultMessage={"contact_supplier"} id={"contact_supplier"} />;
                }
            }

            if (
                reqdFields.ship_to &&
                (isNil(order.Vendor.Ship_To) ||
                    !lengthCheck("Ship_To", "address_line_one") ||
                    !lengthCheck("Ship_To", "city") ||
                    !lengthCheck("Ship_To", "state") ||
                    !lengthCheck("Ship_To", "postal_code"))
            ) {
                if (!isNil(supplierModel.vendor_name_override) && supplierModel.vendor_name_override.length) {
                    const capitalCase = startCase(supplierModel.vendor_name_override);

                    const reason = `Missing ${capitalCase} Ship To Address.`;

                    reasons.ship_to.display = true;
                    reasons.ship_to.message = reason;
                } else {
                    reasons.ship_to.display = true;
                    reasons.ship_to.message = (
                        <OwTranslate defaultMessage={"missing_vendor_ship_to_address"} id={"missing_vendor_ship_to_address"} />
                    );
                }

                if (userModel.isSupplierUser) {
                    disabledLink = (
                        <Link to={supplierVendorsLinkTo}>
                            <OwTranslate defaultMessage={"resolve"} id={"resolve"} />
                        </Link>
                    );
                } else {
                    disabledLink = <OwTranslate defaultMessage={"contact_supplier"} id={"contact_supplier"} />;
                }
            }

            if (reqdFields.email && isNil(order.Vendor.email)) {
                if (!isNil(supplierModel.vendor_name_override) && supplierModel.vendor_name_override.length) {
                    const capitalCase = startCase(supplierModel.vendor_name_override);

                    const reason = `Missing ${capitalCase} Email.`;
                } else {
                    reasons.email.display = true;
                    reasons.email.message = <OwTranslate defaultMessage={"missing_vendor_email"} id={"missing_vendor_email"} />;
                }

                if (userModel.isSupplierUser) {
                    disabledLink = (
                        <a href={supplierVendorsLinkTo}>
                            <OwTranslate defaultMessage={"resolve"} id={"resolve"} />
                        </a>
                    );
                } else {
                    disabledLink = <OwTranslate defaultMessage={"contact_supplier"} id={"contact_supplier"} />;
                }
            }

            const fields = Object.keys(reasons);

            submitBtnDisabledReason = fields.map((field, index) => {
                return <div key={index}>{reasons[`${field}`].message}</div>;
            });
        }
    }

    return { reasons, disabledLink, submitBtnDisabledReason, isSubmitBtnDisabled };
};

const allowSubmit = (
    supplierModel: ISupplierModel,
    userModel: IUserUI,
    order: IOrder,
    hasEditMode: boolean
): { allowSubmit: boolean; reason: string } => {
    const result = {
        allowSubmit: false,
        reason: "",
    };

    const { currentStep, nextStep } = getCurrentAndNextOrderSteps(order, supplierModel);

    if (userModel.isSupplierUser) {
        result.allowSubmit = hasEditMode;

        if (nextStep) {
            if (nextStep.isAdminOnlyStep) {
                if (userModel.userType && userModel.userType.id !== UserTypesEnum.ADMIN) {
                    result.allowSubmit = false;
                }
            }

            if (nextStep.order_state_id === OrderStatesEnum.CONFIRMED && userModel.userType?.id === UserTypesEnum.ORDER_MGMT) {
                result.allowSubmit = true;
            }

            if (userModel.userType?.id === UserTypesEnum.INVENTORY_MGMT) {
                result.allowSubmit = true;
            }
        }

        if (isNil(nextStep)) {
            result.allowSubmit = false;
        }
    } else {
        if (nextStep && nextStep.isVendorStep) {
            result.allowSubmit = true;
        }
    }

    return result;
};

const getSortedWorkflowStatus = (workflows: IWorkflow[]): { name: string; id: number }[] => {
    const combinedStatus: IOrder_Progress_State[] = [];

    for (const workflow of workflows) {
        for (const status of workflow.detail) {
            let found = false;

            for (const existingStatus of combinedStatus) {
                if (existingStatus.order_state_id === status.order_state_id) {
                    found = true;
                    break;
                }
            }

            if (!found) {
                combinedStatus.push(status);
            }
        }
    }

    const sortedStatus = sortBy(combinedStatus, (status) => status.name);

    return sortedStatus.map((status) => {
        return { name: status.name, id: status.order_state_id };
    });
};

/**
 * Given the UoM Set Qty and UoM Set, what is the total Qty from the Base Unit of Measure
 *
 * @param qty
 * @param baseSet
 * @param set
 */
const getUomSetQty = (qty: number, baseSet?: IUnit_Of_Measurement_Set, set?: IUnit_Of_Measurement_Set) => {
    if (baseSet && set) {
        if (set?.id === baseSet.id) {
            return null;
        } else {
            return qty * set.conversion_factor;
        }
    } else {
        return null;
    }
};

const getCurrencySymbol = (currency: string): string => {
    switch (currency) {
        case "USD":
            return "$";

        case "EUR":
            return "€";

        case "GBP":
            return "£";

        case "JPY":
            return "¥";

        case "AUD":
            return "A$";

        case "CAD":
            return "C$";

        case "COP":
            return "COL$";

        default:
            return "$";
    }
};

const convertCurrency = (amount: number, baseCurrency: ICurrency, toCurrency: ICurrency, rateOverride?: number): number => {
    let exchangeRate = baseCurrency.rates?.[toCurrency.name] || 1;

    if (rateOverride) {
        exchangeRate = rateOverride;
    }

    return exchangeRate < 1 ? amount / exchangeRate : amount * exchangeRate;
};

const getCurrencyRate = (baseCurrency: ICurrency, toCurrency: ICurrency): number => {
    const exchangeRate = baseCurrency.rates?.[toCurrency.name] || 1;

    return exchangeRate;
};

const findExistingItemByGuid = (
    order: IOrderUI,
    supplierModel: ISupplierModel,
    catalogItemGuid: string,
    kitDetails?: IKitDetails[],
    isCredit?: boolean
): { hasExistingItem: boolean; existingItem?: IOrder_ItemUI } => {
    let hasExistingItem = false;
    let existingItem: IOrder_ItemUI | undefined;

    // perform dupe check
    if (!kitDetails || !kitDetails.length) {
        if (supplierModel.no_dupe_order_items) {
            for (const orderItem of order.Order_Items) {
                // Line identity is (catalog_item_id, is_credit) — credit add never merges with purchase line
                if (orderItem.Catalog_Item?.guid === catalogItemGuid && !!orderItem.is_credit === !!isCredit) {
                    hasExistingItem = true;
                    existingItem = orderItem;
                    break;
                }
            }
        }
    }

    return { hasExistingItem, existingItem };
};

const findExistingItemById = (
    order: IOrderUI,
    supplierModel: ISupplierModel,
    catalogItemId: number,
    kitDetails?: IKitDetails[],
    isCredit?: boolean
): { hasExistingItem: boolean; existingItem?: IOrder_ItemUI } => {
    let hasExistingItem = false;
    let existingItem: IOrder_ItemUI | undefined;

    // perform dupe check
    if (!kitDetails || !kitDetails.length) {
        if (supplierModel.no_dupe_order_items) {
            for (const orderItem of order.Order_Items) {
                // Line identity is (catalog_item_id, is_credit) — credit add never merges with purchase line
                if (orderItem.Catalog_Item?.id === catalogItemId && !!orderItem.is_credit === !!isCredit) {
                    hasExistingItem = true;
                    existingItem = orderItem;
                    break;
                }
            }
        }
    }

    return { hasExistingItem, existingItem };
};

type DuplicateCheckResult =
    | { action: "allow" }
    | { action: "block"; existingItem: IOrder_ItemUI }
    | { action: "auto_add" }
    | { action: "prompt"; existingItem: IOrder_ItemUI };

const doItemOptionsMatch = (
    existingOptions: { item_option_id: number; value: any }[] | undefined | null,
    newOptions: { item_option_id: number; value: any }[] | undefined | null
): boolean => {
    const existing = existingOptions ?? [];
    const incoming = newOptions ?? [];

    if (existing.length === 0 && incoming.length === 0) return true;
    if (existing.length !== incoming.length) return false;

    const sortedExisting = [...existing].sort((a, b) => a.item_option_id - b.item_option_id);
    const sortedIncoming = [...incoming].sort((a, b) => a.item_option_id - b.item_option_id);

    for (let i = 0; i < sortedExisting.length; i++) {
        if (sortedExisting[i].item_option_id !== sortedIncoming[i].item_option_id) return false;
        if (String(sortedExisting[i].value) !== String(sortedIncoming[i].value)) return false;
    }

    return true;
};

const determineDuplicateAction = (
    supplierModel: ISupplierModel,
    order: IOrderUI,
    catalogItemId: number,
    newItemOptions?: { item_option_id: number; value: any }[] | null,
    kitDetails?: IKitDetails[],
    isCredit?: boolean
): DuplicateCheckResult => {
    // Kit items bypass duplicate check
    if (kitDetails && kitDetails.length) {
        return { action: "allow" };
    }

    let existingItem: IOrder_ItemUI | undefined;

    for (const orderItem of order.Order_Items) {
        // Line identity is (catalog_item_id, is_credit) — a credit add never merges with a purchase line
        if (orderItem.Catalog_Item?.id === catalogItemId && !!orderItem.is_credit === !!isCredit) {
            existingItem = orderItem;
            break;
        }
    }

    if (!existingItem) {
        return { action: "allow" };
    }

    // Strict mode: block duplicates
    if (supplierModel.no_dupe_order_items) {
        return { action: "block", existingItem };
    }

    // Lenient mode: check if options differ
    const optionsMatch = doItemOptionsMatch(existingItem.Order_Item_Options, newItemOptions);

    if (!optionsMatch) {
        return { action: "auto_add" };
    }

    return { action: "prompt", existingItem };
};

const hasEditableDimensions = (order: IOrder, supplierModel: ISupplierModel) => {
    const editableDimensions = {
        width: false,
        height: false,
        depth: false,
    };

    if (order.Catalog) {
        order.Catalog.order_row_config?.forEach((row: IOrderRowConfig) => {
            // lets see if we'll have to have another property to allow non supplier users edit this column value.
            if (row.property === OrderRowColPropertyEnum.DEPTH && row.isSupplierUserEditable) {
                editableDimensions.depth = true;
            } else if (row.property === OrderRowColPropertyEnum.WIDTH && row.isSupplierUserEditable) {
                editableDimensions.width = true;
            } else if (row.property === OrderRowColPropertyEnum.HEIGHT && row.isSupplierUserEditable) {
                editableDimensions.height = true;
            }
        });
    }

    return editableDimensions;
};

/**
 * Returns the step value for qty inputs based on the supplier's qty_precision setting.
 * Optionally falls back to a catalog item's unit_incr_qty if provided.
 */
const getQtyStepValue = (qtyPrecision?: number, unitIncrQty?: number): number => {
    // unit_incr_qty takes priority when explicitly set to something other than the default of 1
    if (unitIncrQty) {
        return unitIncrQty;
    }

    if (qtyPrecision !== undefined && qtyPrecision > 0) {
        switch (qtyPrecision) {
            case 1:
                return 0.1;
            case 2:
                return 0.01;
            case 3:
                return 0.001;
            default:
                return 1;
        }
    }

    return 1;
};

const duplicateOrderEntryItem = async (
    orderGuid: string,
    orderEntryItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnDuplicateOrderEntryItemDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.OrderEntryDuplicateItem,
        params: { orderGuid, orderEntryItemGuid },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
    const response = await fetch(url, { method: "POST", headers });
    if (!response.ok) throw new Error(`Failed to duplicate order entry item: ${response.statusText}`);
    return response.json();
};

const getOrderEntryItems = async (
    orderGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOrder_Item[]> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.OrderEntryGetItems,
        params: { orderGuid },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) throw new Error(`Failed to get order entry items: ${response.statusText}`);
    const data = await response.json();

    return data.order_entry_items;
};

const bulkManualPrice = async (
    orderGuid: string,
    dto: IBulkManualPriceDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<Response> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.OrderEntryBulkManualPrice,
        params: { orderGuid },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
    const response = await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error(`Failed to bulk update manual price: ${response.statusText}`);
    return response;
};

const OrderEntryService = {
    getDefaultOrderSteps,
    getCurrentOrderStep,
    getOrderSteps,
    validateInventoryForItem,
    getPurchaseOrderSteps,
    processTotals,
    calculateTotals,
    getVendorPriceGroupDiscount,
    doTaxCalculation,
    getProfitAndMargin,
    getProfitAndMarginForItem,
    getPricingValue,
    getListPrice,
    getCostPrice,
    getDiscountedPrice,
    getPriceGroupPriceDiscount,
    calculateRowTotal,
    getPriceForCatalogItem,
    getDiscountedPriceForCatalogItem,
    getDiscountedPriceForCartItem,
    getCurrentAndNextOrderSteps,
    isOrderAtEndWorkflowStep,
    isPreviousStepPreliminary,
    getCatalogItemLeadTime,
    segmentOrderStates,
    doInventoryValidation,
    submitBtnDisabled,
    allowSubmit,
    getKitPrice,
    getSortedWorkflowStatus,
    getUomSetQty,
    nonInventoryItemQtyValidation,
    getTotalQtyInOrderItems,
    getItemQtyOnHand,
    getWarehouseQtyForItem,
    getNonWarehouseQtyForItem,
    getWarehouseBackorderedQty,
    determineMaxOrderQty,
    getCurrencySymbol,
    convertCurrency,
    getCurrencyRate,
    findExistingItemById,
    findExistingItemByGuid,
    doItemOptionsMatch,
    determineDuplicateAction,
    hasEditableDimensions,
    getQtyStepValue,
    duplicateOrderEntryItem,
    getOrderEntryItems,
    bulkManualPrice,
};

export default OrderEntryService;
